/**
 * Modal for adding a custom Demonstrate or Explain criterion alongside a template.
 */
import React, { useEffect, useMemo, useState } from "react";
import { translateUi } from "../i18n/index";
import { ClipboardList, Plus, X } from "../icons";
import { GbrModalBackdrop } from "../GbrModalBackdrop";
import { cb, cbCn } from "./cycleBuilderTheme";
import {
  filterLibraryCycleToUnitNumber,
  insertCustomWizardCriteriaWithRenumber,
  libraryCycleFromWizardItems,
  parseCustomUnitNo,
  type LibraryCycle,
} from "./cycleLibraryUi";
import { TemplateUnitsCriteriaScroll } from "./TemplateSelectPanes";
import type { WizardCriteriaItem } from "./CycleWizardStore";

export type CustomCriteriaDraft = {
  unit_no: string;
  element_no: string;
  element: string;
  criteria: string;
  type: "demonstrate" | "explain";
};

export function AddCustomCriteriaModal({
  open,
  type,
  demoLabel,
  explainLabel,
  existingItems,
  templateCycle,
  isDarkMode = false,
  isMobile = false,
  onClose,
  onAdd,
}: {
  open: boolean;
  type: "demonstrate" | "explain" | null;
  demoLabel: string;
  explainLabel: string;
  existingItems: WizardCriteriaItem[];
  /** Framework template used to build the live outline preview. */
  templateCycle: LibraryCycle | null;
  isDarkMode?: boolean;
  isMobile?: boolean;
  onClose: () => void;
  onAdd: (draft: CustomCriteriaDraft) => void;
}) {
  const [unitNo, setUnitNo] = useState("");
  const [elementNo, setElementNo] = useState("");
  const [elementTitle, setElementTitle] = useState("");
  const [criteria, setCriteria] = useState("");

  const columnLabel = type === "explain" ? explainLabel : demoLabel;
  const accent = type === "explain" ? "amber" : "emerald";

  useEffect(() => {
    if (!open || !type) return;
    setUnitNo("");
    setElementNo("");
    setElementTitle("");
    setCriteria("");
  }, [open, type]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const draftItem = useMemo((): WizardCriteriaItem | null => {
    if (!type) return null;
    const unit_no = unitNo.trim();
    const element_no = elementNo.trim();
    const criteriaText = criteria.trim();
    if (!unit_no || !element_no || !criteriaText) return null;
    return {
      unit_no,
      element_no,
      element: elementTitle.trim(),
      criteria: criteriaText,
      type,
      wizardSource: "custom",
    };
  }, [criteria, elementNo, elementTitle, type, unitNo]);

  const previewItems = useMemo(() => {
    if (!draftItem) return existingItems;
    return insertCustomWizardCriteriaWithRenumber(existingItems, draftItem).items;
  }, [draftItem, existingItems]);

  const previewCycle = useMemo(() => {
    if (!templateCycle) return null;
    let cycle = libraryCycleFromWizardItems(previewItems, templateCycle);
    const unitFilter = unitNo.trim();
    if (unitFilter) cycle = filterLibraryCycleToUnitNumber(cycle, unitFilter);
    return cycle;
  }, [previewItems, templateCycle, unitNo]);

  const previewHint = useMemo(() => {
    const unitFilter = unitNo.trim();
    if (!unitFilter) {
      return translateUi("Enter a unit number to focus the outline, or browse all units below.");
    }
    const num = parseCustomUnitNo(unitFilter).number || unitFilter;
    return translateUi(
      "Showing unit {{unit}}. If your element number is already used, later {{column}} rows renumber automatically.",
      { unit: num, column: columnLabel }
    );
  }, [columnLabel, unitNo]);

  const canAdd = draftItem != null;

  if (!open || !type) return null;

  const submit = () => {
    if (!draftItem) return;
    onAdd({
      unit_no: draftItem.unit_no,
      element_no: draftItem.element_no,
      element: draftItem.element ?? "",
      criteria: draftItem.criteria,
      type: draftItem.type,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={cbCn(
          "relative flex max-h-[min(92vh,760px)] w-full flex-col overflow-hidden rounded-t-2xl border shadow-2xl sm:max-w-6xl sm:rounded-2xl",
          isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-custom-criteria-title"
      >
        <GbrModalBackdrop opacity={isDarkMode ? 0.06 : 0.09} />
        <header
          className={cbCn(
            "relative shrink-0 border-b px-4 py-4 sm:px-6",
            isDarkMode ? "border-slate-700 bg-slate-900/95" : "border-slate-100 bg-slate-50/95"
          )}
        >
          <button
            type="button"
            onClick={onClose}
            className={cbCn(
              "absolute right-3 top-3 rounded-lg p-2",
              isDarkMode ? "text-slate-400 hover:bg-slate-800 hover:text-slate-200" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            )}
            aria-label={translateUi("Close")}
          >
            <X className="h-5 w-5" />
          </button>
          <p
            className={cbCn(
              "text-[11px] font-semibold uppercase tracking-wider",
              accent === "emerald"
                ? isDarkMode
                  ? "text-emerald-300"
                  : "text-emerald-800"
                : isDarkMode
                  ? "text-amber-300"
                  : "text-amber-800"
            )}
          >
            {translateUi("Custom criteria")}
          </p>
          <h2
            id="add-custom-criteria-title"
            className={cbCn("mt-0.5 pr-8 text-base font-semibold leading-snug sm:text-lg", isDarkMode ? "text-slate-50" : "text-slate-900")}
          >
            {translateUi("Add {{label}} row", { label: columnLabel })}
          </h2>
          <p className={cbCn("mt-1 text-xs sm:text-sm", cb.bodySm(isDarkMode))}>
            {translateUi(
              "Use the outline on the right to choose unit and element numbers. Your new row appears in the preview once all fields are filled."
            )}
          </p>
        </header>

        <div
          className={cbCn(
            "relative grid min-h-0 flex-1",
            isMobile ? "grid-cols-1" : "grid-cols-[minmax(280px,360px)_minmax(0,1fr)]"
          )}
        >
          <div
            className={cbCn(
              "min-h-0 overflow-y-auto border-b px-4 py-4 sm:px-6 lg:border-b-0 lg:border-r",
              isDarkMode ? "border-slate-700" : "border-slate-200"
            )}
          >
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3">
                <div className="min-w-[5rem] flex-1">
                  <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}>{translateUi("Unit no.")}</label>
                  <input
                    type="text"
                    value={unitNo}
                    onChange={(e) => setUnitNo(e.target.value)}
                    placeholder="1"
                    className={cbCn("mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1", cb.input(isDarkMode))}
                    autoFocus
                  />
                </div>
                <div className="min-w-[5rem] flex-1">
                  <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}>{translateUi("Element no.")}</label>
                  <input
                    type="text"
                    value={elementNo}
                    onChange={(e) => setElementNo(e.target.value)}
                    placeholder="H"
                    className={cbCn("mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1", cb.input(isDarkMode))}
                  />
                </div>
              </div>
              <div>
                <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}>{translateUi("Element title")}</label>
                <input
                  type="text"
                  value={elementTitle}
                  onChange={(e) => setElementTitle(e.target.value)}
                  placeholder={translateUi("Element Title - Optional")}
                  className={cbCn("mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1", cb.input(isDarkMode))}
                />
              </div>
              <div>
                <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}>{translateUi("Criteria text")}</label>
                <textarea
                  value={criteria}
                  onChange={(e) => setCriteria(e.target.value)}
                  rows={5}
                  placeholder={translateUi("What the assessor will look for…")}
                  className={cbCn(
                    "mt-1 w-full resize-y rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1",
                    cb.input(isDarkMode)
                  )}
                />
              </div>
            </div>
          </div>

          <aside
            className={cbCn(
              "flex min-h-0 flex-col",
              isMobile ? "max-h-[min(42vh,360px)]" : "",
              isDarkMode ? "bg-slate-950/40" : "bg-slate-50/80"
            )}
          >
            <div
              className={cbCn(
                "shrink-0 border-b px-4 py-3 sm:px-4",
                isDarkMode ? "border-slate-700 bg-slate-900/80" : "border-slate-200 bg-white/90"
              )}
            >
              <div className="flex items-start gap-2">
                <span
                  className={cbCn(
                    "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                    isDarkMode ? "bg-indigo-500/20 text-indigo-200" : "bg-indigo-100 text-indigo-700"
                  )}
                  aria-hidden
                >
                  <ClipboardList className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className={cbCn("text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-indigo-300" : "text-indigo-700")}>
                    {translateUi("Cycle outline")}
                  </p>
                  <p className={cbCn("mt-0.5 text-xs leading-snug", cb.bodySm(isDarkMode))}>{previewHint}</p>
                </div>
              </div>
            </div>
            {previewCycle && previewCycle.units.length > 0 ? (
              <TemplateUnitsCriteriaScroll
                cycle={previewCycle}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
                className="min-h-0 flex-1"
              />
            ) : (
              <div className={cbCn("grid flex-1 place-items-center px-4 py-10 text-center text-sm", cb.bodySm(isDarkMode))}>
                {templateCycle
                  ? translateUi("No outline to show yet.")
                  : translateUi("Select a template to see its outline here.")}
              </div>
            )}
          </aside>
        </div>

        <footer
          className={cbCn(
            "relative flex shrink-0 flex-col gap-2 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6",
            isDarkMode ? "border-slate-700 bg-slate-900/95" : "border-slate-100 bg-slate-50/95"
          )}
        >
          <p className={cbCn("text-xs", cb.bodySm(isDarkMode))}>
            {translateUi("Unit no., element no., and criteria text are all required. Duplicate element numbers shift later rows down.")}
          </p>
          <div className="flex shrink-0 justify-end gap-2">
            <button type="button" onClick={onClose} className={cbCn("rounded-lg px-4 py-2 text-sm font-semibold", cb.btnSecondary(isDarkMode))}>
              {translateUi("Cancel")}
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canAdd}
              className={cbCn(
                "inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50",
                isMobile && "min-h-[44px]",
                cb.accentBtn(isDarkMode, accent)
              )}
            >
              <Plus className="h-4 w-4 shrink-0" />
              {translateUi("Add to cycle")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
